import { type LocaleCode, supportedLocales } from '../../config/locales';

// Single, stable storage key for the visitor's chosen locale. Owned solely by
// this module (no other namespace may read or write it). See requirement 1.4.
export const LOCALE_STORAGE_KEY = 'gma-locale' as const;

function isSupportedLocale(value: string): value is LocaleCode {
  return (supportedLocales as readonly string[]).includes(value);
}

// Persist the visitor's chosen locale code to client-side storage.
// SSR-safe and defensive: silently no-ops when `window` is unavailable or
// when storage throws (quota, private-browsing, disabled storage, etc.).
// Synchronous: returns after a single setItem call (no debouncing, no batching).
export function persistLocale(code: LocaleCode): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, code);
  } catch {
    // R7.2: silently swallow storage errors so navigation/rendering continues.
  }
}

// Read the visitor's previously stored locale, validated against the current
// supported-locales list. Returns `null` for missing, empty, malformed, or
// unsupported values, and for read errors (R7.1, R7.3). SSR-safe.
export function readPersistedLocale(): LocaleCode | null {
  if (typeof window === 'undefined') {
    return null;
  }
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw == null || raw === '') {
    return null;
  }
  return isSupportedLocale(raw) ? raw : null;
}
